import type { KeyboardEvent, PointerEvent } from 'react'
import { useCallback, useRef, useState } from 'react'

export interface UseResizableWidthOptions {
  minWidth?: number
  maxWidth?: number
  defaultWidth?: number
  step?: number
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

/**
 * Tracks a resizable width for a right-anchored drag handle. The pointer is
 * captured on `handlePointerDown`, so the drag keeps tracking and ends
 * correctly even when the pointer leaves the window or crosses an `<iframe>`;
 * `handlePointerMove` sets the width to the pointer position clamped to
 * `[minWidth, maxWidth]`, and the drag ends on `handlePointerUp`.
 *
 * `handleKeyDown` is the keyboard equivalent: Left grows, Right shrinks
 * (mirroring the right-anchored drag direction) by `step`, Home resets.
 * `resetToDefault` snaps the width back to `defaultWidth`.
 *
 * @returns the current `width` (px), the resolved `minWidth`/`maxWidth` bounds,
 *   the pointer handlers for the drag handle, `handleKeyDown`, and
 *   `resetToDefault`.
 */
const useResizableWidth = ({
  minWidth = 320,
  maxWidth = 1200,
  defaultWidth = 500,
  step = 16
}: UseResizableWidthOptions = {}) => {
  const [width, setWidth] = useState(defaultWidth)
  const draggingRef = useRef(false)

  const handlePointerDown = useCallback((e: PointerEvent) => {
    e.currentTarget.setPointerCapture?.(e.pointerId)
    draggingRef.current = true
  }, [])

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!draggingRef.current) return
      // No button held means the release was missed (e.g. a pointercancel
      // ended the gesture); stop tracking instead of resizing on hover.
      if (e.buttons === 0) {
        draggingRef.current = false
        return
      }
      setWidth(clamp(window.innerWidth - e.clientX, minWidth, maxWidth))
    },
    [minWidth, maxWidth]
  )

  const handlePointerUp = useCallback((e: PointerEvent) => {
    draggingRef.current = false
    e.currentTarget.releasePointerCapture?.(e.pointerId)
  }, [])

  const handlePointerCancel = useCallback(() => {
    // pointercancel has already released the capture, so just end the drag.
    draggingRef.current = false
  }, [])

  const nudgeWidth = useCallback(
    (delta: number) => setWidth((w) => clamp(w + delta, minWidth, maxWidth)),
    [minWidth, maxWidth]
  )

  const resetToDefault = useCallback(() => setWidth(defaultWidth), [defaultWidth])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        nudgeWidth(step)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        nudgeWidth(-step)
      } else if (e.key === 'Home') {
        e.preventDefault()
        resetToDefault()
      }
    },
    [nudgeWidth, resetToDefault, step]
  )

  return {
    width,
    minWidth,
    maxWidth,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    handleKeyDown,
    resetToDefault
  }
}

export default useResizableWidth
